/**
 * What the *wallet's* node says, as opposed to what ours does.
 *
 * Mode B funds a wallet against gas figures the portal reads from its own RPC
 * list. MetaMask prices the same transaction against whichever node its network
 * entry points at, and those are not the same node: measured on chain 97 on
 * 2026-08-28, `bnbchain.org` and `publicnode` quoted 0,1 gwei while `drpc.org`
 * quoted 1 gwei for the same block. A tenfold difference in the quote is a
 * tenfold difference in what the wallet insists on holding, and the user meets
 * it as a greyed-out confirm button with no number attached.
 *
 * So this module stops inferring and asks. Every read here goes through the
 * injected provider — the same path MetaMask's own fee UI takes — and the
 * answers are rendered by the staging diagnostic panel next to the portal's
 * figures, so the two can be compared rather than argued about.
 *
 * Nothing here signs, sends, or logs anything a signature could be recovered
 * from: the recorder below refuses every signing method by name.
 */
import type { Address, EIP1193Provider, Hex } from "viem";

/** Methods whose params may carry a message, a signature, or a key. Never
 *  recorded, whatever else changes. */
const NEVER_RECORDED = new Set([
  "eth_sign",
  "personal_sign",
  "eth_signTypedData",
  "eth_signTypedData_v3",
  "eth_signTypedData_v4",
  "eth_signTransaction",
  "eth_decrypt",
  "eth_getEncryptionPublicKey",
  "wallet_requestPermissions",
  "wallet_watchAsset",
]);

/** The transaction fields worth showing. `data` is deliberately absent: it is
 *  long, it is already known from the ABI, and it is not what is in question. */
export type RecordedRequest = {
  method: string;
  at: number;
  from?: string;
  to?: string;
  value?: string;
  gas?: string;
  gasPrice?: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  /** Bytes of calldata, rather than the calldata. */
  dataBytes?: number;
};

type TransactionParam = {
  from?: string;
  to?: string;
  value?: string;
  gas?: string;
  gasPrice?: string;
  maxFeePerGas?: string;
  maxPriorityFeePerGas?: string;
  data?: string;
};

function describe(method: string, params: unknown): RecordedRequest | null {
  if (NEVER_RECORDED.has(method)) return null;
  if (method !== "eth_sendTransaction" && method !== "eth_estimateGas") {
    return null;
  }
  const first = Array.isArray(params) ? (params[0] as TransactionParam) : null;
  if (!first || typeof first !== "object") return null;

  return {
    method,
    at: Date.now(),
    from: first.from,
    to: first.to,
    value: first.value ?? "0x0",
    gas: first.gas,
    gasPrice: first.gasPrice,
    maxFeePerGas: first.maxFeePerGas,
    maxPriorityFeePerGas: first.maxPriorityFeePerGas,
    dataBytes:
      typeof first.data === "string"
        ? Math.max(0, (first.data.length - 2) / 2)
        : undefined,
  };
}

const listeners = new Set<(request: RecordedRequest) => void>();
let last: RecordedRequest | null = null;

export function lastWalletRequest(): RecordedRequest | null {
  return last;
}

export function onWalletRequest(
  listener: (request: RecordedRequest) => void,
): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Wraps a provider so the parameters the dapp hands the wallet are observable.
 *
 * What the dapp sends is the interesting half of the disagreement: viem defers
 * gas and fees to the wallet for injected accounts, so `eth_sendTransaction`
 * leaves here with no `gas` and no price at all — every figure MetaMask shows
 * is its own. Recording the request proves that rather than asserting it.
 */
export function observeWalletRequests(
  provider: EIP1193Provider,
): EIP1193Provider {
  const wrapped = new Proxy(provider, {
    get(target, property, receiver) {
      if (property !== "request") return Reflect.get(target, property, receiver);
      return async (args: { method: string; params?: unknown }) => {
        const recorded = describe(args.method, args.params);
        if (recorded) {
          last = recorded;
          for (const listener of listeners) listener(recorded);
        }
        return (target.request as EIP1193Provider["request"])(
          args as Parameters<EIP1193Provider["request"]>[0],
        );
      };
    },
  });
  return wrapped as EIP1193Provider;
}

/** What the wallet's own node answers for this transaction. */
export type WalletGasView = {
  chainId: number | null;
  balanceWei: bigint | null;
  gasPriceWei: bigint | null;
  baseFeePerGasWei: bigint | null;
  maxPriorityFeePerGasWei: bigint | null;
  estimateGas: bigint | null;
  /** Errors are answers too: a wallet whose node cannot estimate will not let
   *  anybody confirm, and that looks identical to being short of BNB. */
  errors: string[];
};

/**
 * Wallet RPC errors are rarely `Error` instances. MetaMask hands back a plain
 * object with `code` and `message`, and stringifying it produced "[object
 * Object]" in the panel — a line that says something went wrong and nothing
 * about what, which is the one thing a diagnostic must not do.
 */
function describeError(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object") {
    const shaped = error as { code?: unknown; message?: unknown; data?: unknown };
    const parts: string[] = [];
    if (shaped.code !== undefined) parts.push(`code ${String(shaped.code)}`);
    if (typeof shaped.message === "string") parts.push(shaped.message);
    if (parts.length > 0) return parts.join(": ");
    try {
      return JSON.stringify(error);
    } catch {
      return "unserialisable error object";
    }
  }
  return String(error ?? "unknown");
}

async function ask(
  provider: EIP1193Provider,
  method: string,
  params: unknown[],
  errors: string[],
): Promise<string | null> {
  try {
    const value = await provider.request({
      method,
      params,
    } as Parameters<EIP1193Provider["request"]>[0]);
    return typeof value === "string" ? value : null;
  } catch (error) {
    errors.push(`${method}: ${describeError(error).split("\n")[0].slice(0, 160)}`);
    return null;
  }
}

const toBigInt = (value: string | null): bigint | null => {
  if (!value) return null;
  try {
    return BigInt(value);
  } catch {
    return null;
  }
};

export async function readWalletGasView(
  provider: EIP1193Provider,
  call: { from: Address; to: Address; data: Hex },
): Promise<WalletGasView> {
  const errors: string[] = [];

  const [chainId, balance, gasPrice, priority, estimate] = await Promise.all([
    ask(provider, "eth_chainId", [], errors),
    ask(provider, "eth_getBalance", [call.from, "latest"], errors),
    ask(provider, "eth_gasPrice", [], errors),
    ask(provider, "eth_maxPriorityFeePerGas", [], errors),
    ask(
      provider,
      "eth_estimateGas",
      [{ from: call.from, to: call.to, data: call.data, value: "0x0" }],
      errors,
    ),
  ]);

  let baseFee: bigint | null = null;
  try {
    const block = (await provider.request({
      method: "eth_getBlockByNumber",
      params: ["latest", false],
    } as Parameters<EIP1193Provider["request"]>[0])) as {
      baseFeePerGas?: string;
    } | null;
    baseFee = toBigInt(block?.baseFeePerGas ?? null);
  } catch (error) {
    errors.push(
      `eth_getBlockByNumber: ${describeError(error).split("\n")[0].slice(0, 160)}`,
    );
  }

  return {
    chainId: chainId === null ? null : Number(BigInt(chainId)),
    balanceWei: toBigInt(balance),
    gasPriceWei: toBigInt(gasPrice),
    baseFeePerGasWei: baseFee,
    maxPriorityFeePerGasWei: toBigInt(priority),
    estimateGas: toBigInt(estimate),
    errors,
  };
}

/**
 * What a wallet quoting these numbers would insist on holding.
 *
 * Two candidates, because a wallet on an EIP-1559 chain reserves against its
 * max fee rather than the price it expects to pay, and chain 97 answers both
 * questions. The larger one is the one that greys out the button.
 */
export function walletRequirementWei(
  view: WalletGasView,
  gasLimitBps: bigint,
): bigint | null {
  if (view.estimateGas === null) return null;
  const limit = (view.estimateGas * gasLimitBps) / 10_000n;

  const legacy = view.gasPriceWei ?? 0n;
  const priority = view.maxPriorityFeePerGasWei ?? 0n;
  const base = view.baseFeePerGasWei ?? 0n;
  const eip1559 = base * 2n + priority;

  const fee = eip1559 > legacy ? eip1559 : legacy;
  if (fee === 0n) return null;
  return limit * fee;
}
